import React, { FC, useEffect, useRef, useState } from 'react'

import { ComponentLoadingContainer } from '../../../models/components/loadingContainer'
import { ComponentVideoRecording } from '../../../models/components/videoRecording'
import { extractTextFromRichText } from '../../../utils/functions.utils'
import { findTarget } from '../../../utils/microcopy.utils'
import Typography from '../../global/typography'
import Button from '../button'
import { VideoRecordingContainer, VideoRecordingList, VideoRecordingNotFound } from './style'
import CategoryFilters from './subcomponent/categoryFilters'
import Loader from './subcomponent/Loader'
import SearchFilters from './subcomponent/searchFilter'
import VideoCardList from './subcomponent/videoCardList'
import VideoShareModal from './subcomponent/videoShareModal'

type Props = {
  data: ComponentLoadingContainer
}

export const LoadingContainer: FC<Props> = ({ data }) => {
  if (!data) return null
  /* Microcopy Values */
  const searchPlaceholder =
    findTarget(`recording.search.placeholder.text`, data?.microcopy?.values || [], true) || ''
  const allRecordings =
    findTarget(`recording.filters.all.text`, data?.microcopy?.values || [], true) || ''
  const recordingNotFoundHeading =
    findTarget(`recording.not.found.heading.text`, data?.microcopy?.values || [], true) || ''
  const recordingNotFoundDescription =
    findTarget(`recording.not.found.description.text`, data?.microcopy?.values || [], true) || ''

  const [filterData, setFilterData] = useState<ComponentVideoRecording[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string | null>(allRecordings)
  const referenceRef = useRef<HTMLDivElement>(null)
  const [visibleItems, setVisibleItems] = useState(4)
  const [loading, setLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalContent, setModalContent] = useState({})
  const [searchKeyword, setSearchKeyword] = useState('')
  const [closeIcon, setCloseIcon] = useState(false)
  const [notFoundMessage, setNotFoundMessage] = useState(false)
  // Share video modal functions
  const openModal = (content: any) => {
    let sectionTop = 0
    let sectionLeft = 0
    const cardRect = referenceRef.current?.getBoundingClientRect()
    if (cardRect) {
      sectionTop = cardRect.top + window.scrollY
      sectionLeft = cardRect.left + window.scrollX
    }
    content.position.top = content.position.top - sectionTop
    content.position.left = content.position.left - (sectionLeft !== 0 ? sectionLeft + 125 : 180)
    setModalContent(content)
    setIsModalOpen(!isModalOpen)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setModalContent({})
  }

  // Load more cards function
  const loadMore = () => {
    setLoading(true)
    setTimeout(() => {
      data?.items?.length !== visibleItems &&
        setVisibleItems(prevVisibleItems => prevVisibleItems + 4)
      setLoading(false)
    }, 500)
  }

  // Category filters related functions
  const handleCategoryCount = (data: ComponentVideoRecording[]) => {
    const grouped = data?.reduce(
      (
        acc: {
          [key: string]: { label: string; count: number; recordings: ComponentVideoRecording[] }
        },
        card
      ) => {
        if (!acc[card.label]) {
          acc[card.label] = {
            label: card.label,
            count: 0,
            recordings: [],
          }
        }
        acc[card.label].count += 1
        acc[card.label].recordings.push(card)
        return acc
      },
      {}
    )
    // Convert grouped object to an array and update state
    setGroupedRecordings(Object.values(grouped))
  }

  // State to store grouped recordings by category
  const [groupedRecordings, setGroupedRecordings] = useState<
    { label: string; count: number; recordings: ComponentVideoRecording[] }[]
  >([])

  const filterCategoryData = (category: string) => {
    setVisibleItems(4)
    setSelectedCategory(category)
    let categoryData

    if (category !== allRecordings) {
      categoryData = data?.items?.filter(item => item?.label === category)
    } else {
      if (searchKeyword.length === 0) {
        categoryData = data?.items
        handleCategoryCount(data?.items)
      } else {
        handleSearch()
        return
      }
    }
    setFilterData(categoryData || [])
  }

  // Search related functions
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value)
  }

  const handleSearch = () => {
    if (searchKeyword.length > 2) {
      const searchResult = data?.items?.filter(
        item =>
          item?.title?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
          item?.speakers?.some((speaker: any) =>
            `${speaker?.firstName?.trim()} ${speaker?.lastName?.trim()}`
              .toLowerCase()
              .includes(searchKeyword.toLowerCase())
          ) ||
          item?.description?.raw?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
          extractTextFromRichText(item?.description)
            .toLowerCase()
            .includes(searchKeyword.toLowerCase())
      )
      setFilterData(searchResult)
      setCloseIcon(true)
      setSelectedCategory(allRecordings)
      handleCategoryCount(searchResult)
      searchResult?.length === 0 && setNotFoundMessage(true)
    }
  }

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearch()
    }
    if (searchKeyword.length === 0) {
      setSearchKeyword('')
      setCloseIcon(false)
      setFilterData(data?.items)
      handleCategoryCount(data?.items)
      setSelectedCategory(allRecordings)
      setNotFoundMessage(false)
    } else {
      setCloseIcon(true)
    }
  }

  const clearSearch = () => {
    setSearchKeyword('')
    setCloseIcon(false)
    setFilterData(data?.items)
    handleCategoryCount(data?.items)
    setSelectedCategory(allRecordings)
    setNotFoundMessage(false)
  }

  // Functionality on page load
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search)
    const videoId = queryParams.get('videoId')

    if (videoId) {
      const singleRecordingData = data?.items?.find(recording => {
        return (
          recording?.video?.youtubeId === videoId ||
          recording?.video?.vimeoId === videoId ||
          recording?.video?.tencentVideoUrl?.includes(videoId)
        )
      })
      setFilterData(singleRecordingData ? [singleRecordingData] : data?.items)
      handleCategoryCount(singleRecordingData ? [singleRecordingData] : data?.items)
      setSelectedCategory(singleRecordingData?.label || allRecordings)
    } else {
      setFilterData(data?.items)
      handleCategoryCount(data?.items)
      setSelectedCategory(allRecordings)
    }

    window.onclick = function (e) {
      if (
        e.target &&
        !(e.target as Element).closest('.Video__Share-Popup') &&
        (e.target as Element).className !== 'Video__Share'
      ) {
        setIsModalOpen(false)
      }
    }
  }, [])

  // Search Filter Object Data
  const searchFilterData = {
    handleSearchChange: handleSearchChange,
    handleKeyUp: handleKeyUp,
    searchKeyword: searchKeyword,
    searchPlaceholder: searchPlaceholder,
    closeIcon: closeIcon,
    clearSearch: clearSearch,
    handleSearch: handleSearch,
  }

  // Category Filter Object Data
  const categoryFilterData = {
    selectedCategory: selectedCategory || '',
    allRecordings: allRecordings,
    groupedRecordings: groupedRecordings,
    filterCategoryData: filterCategoryData,
  }

  return (
    <VideoRecordingContainer
      className="VideoRecordingContainer"
      ref={referenceRef}
      data-cy="LoadingContainer">
      {data?.search && <SearchFilters searchFilterData={searchFilterData} />}

      {data?.filter && groupedRecordings.length !== 0 && (
        <CategoryFilters categoryFilterData={categoryFilterData} />
      )}

      {filterData.length !== 0 && (
        <VideoRecordingList className="Video__Recording-List">
          {filterData?.slice(0, visibleItems).map((item: any, index: number) => {
            return (
              <VideoCardList
                key={`${item?.id + index}`}
                data={item}
                index={index}
                openModal={openModal}
              />
            )
          })}
        </VideoRecordingList>
      )}

      {isModalOpen && <VideoShareModal content={modalContent} closeModal={closeModal} />}

      {loading && <Loader loading={loading} />}

      {visibleItems < filterData.length && (
        <div className="Load__More-Speakers">
          <div className="Button__Box">
            <Button
              icon={true}
              href={''}
              text={data?.loadMore}
              type="tertiary"
              size="small"
              theme={'light'}
              clickHandler={loadMore}
            />
          </div>
        </div>
      )}

      {filterData.length === 0 && notFoundMessage && (
        <VideoRecordingNotFound>
          <Typography type="header5">{`${recordingNotFoundHeading} "${searchKeyword}"`}</Typography>
          <Typography
            type="bodyXS"
            className="font__inter">{`${recordingNotFoundDescription}`}</Typography>
        </VideoRecordingNotFound>
      )}
    </VideoRecordingContainer>
  )
}
